import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import { makeStyles, Theme } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import AddPhotoAlternateIcon from "@material-ui/icons/AddPhotoAlternate";
import CloseIcon from "@material-ui/icons/Close";
import ImageIcon from "@material-ui/icons/Image";
import ArrowLeftIcon from "@material-ui/icons/KeyboardArrowLeft";
import ArrowRightIcon from "@material-ui/icons/KeyboardArrowRight";
import SendIcon from "@material-ui/icons/Send";
import { RefObject, useEffect, useRef, useState } from "react";
import { sleepAsync } from "../../../../../../../../../common/functions";
import { useScreenSize } from "../../../../../../../../../common/hooks/useScreenSize";
import { swap } from "../../../../../../../../../common/util/Array/swap";
import { isAndroid } from "../../../../../../../../../common/util/osCheck";
import { PrimaryButton } from "../../../../../../../Button/PrimaryButton";
import { CollapseWithUnmount } from "../../../../../../../Collapse/CollapseWithUnmount";
import { openOtherUserPanel } from "../../../../../../../User/OtherUserPanel/functions";
import { User } from "../../../../../../../User/types";
import { UserAvatar } from "../../../../../../../User/UserAvatar/UserAvatar";
import {
    validateImage,
    validImageContentTypes,
} from "../../../../../../../User/UserAvatar/validateImage";

export type SocialInputReturn = undefined | "shouldNotClearField";
export function InputFieldArea({
    user,
    sendText,
    className,
    placeholder,
    disabled,
    onEdit,
    autoFocus,
    inputRef,
    includeQuestion,
}: {
    user?: User;
    sendText: (text: string, file: File[]) => Promise<SocialInputReturn>;
    className?: string;
    placeholder?: string;
    disabled?: boolean;
    onEdit?: () => void;
    autoFocus?: boolean;
    inputRef?: RefObject<HTMLInputElement>;
    includeQuestion?: boolean;
}) {
    const { screenWidth } = useScreenSize();
    const isWide = screenWidth >= 768;

    const [text, setText] = useState("");
    const [files, setFiles] = useState<File[]>([]);
    const [removeQuestion, setRemoveQuestion] = useState(!includeQuestion);

    const error = validate(text, files);

    const imageInputRef = useRef<HTMLInputElement>(null);

    const [isFocusedStyle, setIsFocusedStyle] = useState(
        !!autoFocus && isAndroid // autoFocusをするとアンドロイドでソフトウェアキーボードが出てしまう問題があるため、スタイルだけ最初からフォーカスしたような見た目にする
    );

    const isSendButtonDisabled =
        (!text.trim() && files.length < 1) || !!error || !!disabled;
    const c = useCommentFieldStyles({
        isWide,
        isSendButtonDisabled,
        isPlaceholderLoaded: !!placeholder,
        isFocusedStyle,
    });

    const onEditContainer = useRef(() => {});
    if (onEdit) {
        onEditContainer.current = onEdit;
    }
    useEffect(() => {
        if (typeof onEditContainer.current === "function") {
            const intervalId = setInterval(() => {
                onEditContainer.current();
            }, 50);
            sleepAsync(1000).then(() => {
                clearInterval(intervalId);
            });
        }
    }, [onEditContainer, text, files]);

    return (
        <div className={className}>
            <div className={c.container}>
                <UserAvatar
                    user={user}
                    colorNumber={user?.userId ?? "noColor"}
                    size={40}
                    className={user ? "pointer" : undefined}
                    onClick={() => {
                        if (!user) {
                            return;
                        }
                        openOtherUserPanel(user.userId);
                    }}
                />

                <div className="fullWidth">
                    <CollapseWithUnmount in={!!text && !removeQuestion}>
                        <blockquote className={c.questionBlockquote}>
                            {placeholder}
                            <br />
                            <Button
                                startIcon={<CloseIcon />}
                                size="small"
                                variant="outlined"
                                className={c.removeQuestionButton}
                                onClick={() => {
                                    setRemoveQuestion(true);
                                    inputRef?.current?.focus();
                                }}
                            >
                                Remove this question
                            </Button>
                        </blockquote>
                    </CollapseWithUnmount>

                    <div className={c.textFieldAndSendContainer}>
                        <TextField
                            autoFocus={autoFocus && !isAndroid} // autoFocusをするとアンドロイドでソフトウェアキーボードが出てしまう問題があるため、スタイルだけ最初からフォーカスしたような見た目にする
                            placeholder={placeholder}
                            value={text}
                            multiline
                            maxRows={6}
                            fullWidth
                            className={c.textField}
                            onChange={ev => {
                                setText(ev.target.value || "");
                            }}
                            inputProps={{
                                className: c.input,
                                ref: inputRef,
                            }}
                            helperText={
                                error ? (
                                    <div className={c.errorText}>{error}</div>
                                ) : undefined
                            }
                            error={!!error}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment
                                        position="end"
                                        className={c.inputAdornment}
                                    >
                                        <IconButton
                                            aria-label="image"
                                            onClick={() => {
                                                imageInputRef.current?.click();
                                            }}
                                            className={c.imageIconButton}
                                        >
                                            <ImageIcon
                                                className={c.imageIcon}
                                            />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            onBlur={() => {
                                setIsFocusedStyle(false);
                            }}
                        />

                        <div className={c.buttonContainer}>
                            <PrimaryButton
                                className={c.sendButton}
                                onClick={async () => {
                                    if (isSendButtonDisabled) {
                                        return;
                                    }
                                    const previousText = text;
                                    const previousFiles = files;

                                    const trimmedText = text.trim();
                                    const sendResult = sendText(
                                        removeQuestion
                                            ? trimmedText
                                            : `> ${placeholder}
${trimmedText}`,
                                        files
                                    ); // Send

                                    setText("");
                                    setFiles([]);
                                    if (imageInputRef.current) {
                                        imageInputRef.current.value = "";
                                    }

                                    if (
                                        (await sendResult) ===
                                        "shouldNotClearField"
                                    ) {
                                        setText(previousText);
                                        setFiles(previousFiles);
                                    }
                                }}
                            >
                                <SendIcon className={c.sendIcon} />
                            </PrimaryButton>
                        </div>
                    </div>
                </div>
            </div>

            {files.length > 0 && (
                <div className={c.previewContainer}>
                    {files.map((file, index) => (
                        <div key={index} className={c.imageContainer}>
                            <img
                                alt={"image to upload"}
                                className={c.previewImage}
                                src={URL.createObjectURL(file)}
                            />
                            <IconButton
                                onClick={() => {
                                    setFiles(
                                        files.filter((f, i) => i !== index)
                                    );
                                    if (imageInputRef.current) {
                                        imageInputRef.current.value = "";
                                    }
                                }}
                                className={c.removeImageIconButton}
                            >
                                <CloseIcon />
                            </IconButton>

                            {index > 0 && (
                                <IconButton
                                    className={c.leftArrowButton}
                                    onClick={() => {
                                        setFiles(swap(files, index, index - 1));
                                    }}
                                >
                                    <ArrowLeftIcon />
                                </IconButton>
                            )}
                            {index < files.length - 1 && (
                                <IconButton
                                    className={c.rightArrowButton}
                                    onClick={() => {
                                        setFiles(swap(files, index, index + 1));
                                    }}
                                >
                                    <ArrowRightIcon />
                                </IconButton>
                            )}
                        </div>
                    ))}
                    <IconButton
                        className={c.addPhotoButton}
                        onClick={() => {
                            imageInputRef.current?.click();
                        }}
                    >
                        <AddPhotoAlternateIcon className={c.addPhotoIcon} />
                    </IconButton>
                </div>
            )}
            <input
                hidden
                type="file"
                accept={validImageContentTypes.join(",")}
                ref={imageInputRef}
                onChange={({ target }) => {
                    if (!target.files || !target.files[0]) {
                        return;
                    }
                    const targetFiles = Array.from(target.files);
                    const err = targetFiles.reduce<string | null>(
                        (acc, file) => {
                            return acc || validateImage(file);
                        },
                        null
                    );
                    if (err) {
                        alert(err);
                        return;
                    }
                    setFiles([...files, ...targetFiles]);
                    if (imageInputRef.current) {
                        imageInputRef.current.value = "";
                    }
                }}
                multiple
            />
        </div>
    );
}

const sendButtonSize = 39;
const imageButtonSize = 30;
const useCommentFieldStyles = makeStyles<
    Theme,
    {
        isWide: boolean;
        isSendButtonDisabled: boolean;
        isPlaceholderLoaded: boolean;
        isFocusedStyle: boolean;
    }
>(theme => ({
    container: {
        display: "flex",
    },
    questionBlockquote: {
        fontSize: 15,
        color: "#6a737d",
        borderTopRightRadius: 5,
        borderBottomRightRadius: 5,
        padding: "5px 10px",
        borderLeft: "10px solid #eee",
        marginLeft: 10,
        marginBottom: 10,
    },
    removeQuestionButton: {
        fontSize: "small",
        maxHeight: 25,
        position: "relative",
        top: 4,
        color: "#6a737d",
    },
    textFieldAndSendContainer: { display: "flex" },
    input: ({ isWide, isPlaceholderLoaded }) => ({
        ...(isWide
            ? {
                  paddingTop: 10,
                  marginTop: -6,
              }
            : { paddingTop: 10, marginTop: -8 }),
        transition: "all 500ms",
        opacity: isPlaceholderLoaded ? 1 : 0,
        fontSize: 17, // To prevent iPhone from focusing by making this more than 16
        lineHeight: 1.3,
    }),
    textField: ({ isWide, isFocusedStyle }) => ({
        padding: 0,
        width: "100%",
        marginLeft: 10,
        marginTop: isWide ? 0 : 2,
        "& div:before": isFocusedStyle
            ? {
                  borderBottom: `solid 2px ${theme.palette.primary.main}`,
              }
            : {},
    }),
    errorText: { color: theme.palette.secondary.main, fontWeight: "bold" },
    buttonContainer: ({ isSendButtonDisabled }) => ({
        width: isSendButtonDisabled ? 0 : sendButtonSize,
        minWidth: isSendButtonDisabled ? 0 : sendButtonSize,
        marginLeft: isSendButtonDisabled ? 10 : 0,
        overflow: "hidden",
        display: "flex",
        minHeight: "100%",
        justifyContent: "center",
        alignItems: "center",
        position: "relative",
        right: 2,
        transition: "all 200ms",
        marginBottom: 2,
    }),
    sendButton: {
        maxHeight: sendButtonSize,
        minHeight: sendButtonSize,
        maxWidth: sendButtonSize,
        minWidth: sendButtonSize,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: 4,
    },
    sendIcon: {
        height: 20,
        width: 20,
        color: "white",
    },
    inputAdornment: {
        marginRight: 5,
        paddingTop: 2,
    },
    imageIconButton: {
        maxHeight: imageButtonSize,
        minHeight: imageButtonSize,
        maxWidth: imageButtonSize,
        minWidth: imageButtonSize,
    },
    imageIcon: { width: 30, height: 30 },
    imageContainer: {
        position: "relative",
        display: "flex",
        alignItems: "center",
    },
    removeImageIconButton: {
        position: "absolute",
        right: 1,
        top: 1,
        width: 20,
        height: 20,
        backgroundColor: "rgba(255,255,255,0.7)",
        color: "black",
    },
    leftArrowButton: {
        position: "absolute",
        left: 1,
        bottom: 1,
        width: 20,
        height: 20,
        color: "black",
        backgroundColor: "rgba(255,255,255,0.7)",
        "&:hover": {
            backgroundColor: "rgba(255,255,255,0.6)",
        },
    },
    rightArrowButton: {
        position: "absolute",
        right: 1,
        bottom: 1,
        width: 20,
        height: 20,
        color: "black",
        backgroundColor: "rgba(255,255,255,0.7)",
        "&:hover": {
            backgroundColor: "rgba(255,255,255,0.6)",
        },
    },
    addPhotoButton: {
        width: 35,
        height: 35,
        margin: "auto 25px auto 10px",
    },
    addPhotoIcon: { width: 30, height: 30 },
    previewContainer: {
        marginLeft: 50,
        marginRight: 2,
        display: "flex",
        gap: 10,
        padding: 2,
        backgroundColor: theme.palette.grey[200],
        overflowX: "auto",
    },
    previewImage: {
        height: 100,
        objectFit: "contain",
    },
}));

const maxTextLength = 300;
const maxUploadableFiles = 8; // If this value is changed, don't forget to change the length of ImageExtensions in DB tables.
function validate(text: string, files: File[]) {
    if (text.length > maxTextLength) {
        return `Text must be less than or equal to ${maxTextLength} characters`;
    }
    if (files.length > maxUploadableFiles) {
        return `Sorry! You cannot select more than ${maxUploadableFiles} files!`;
    }
    return undefined;
}
